import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/api";
import Swal from "sweetalert2";
import LoadingSpinnerModal from "../LoadingSpinnerModal";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import tr from "date-fns/locale/tr";

const FinancialRegisterTransferModal = ({
  onCancel = () => {},
  selectedOpenModalId,
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [registers, setRegisters] = useState(null);
  const [token, setToken] = useState(null);
  const [customerID, setCustomerID] = useState(Number());
  const [loading, setLoading] = useState(true);
  const [exitRegisterID, setExitRegisterID] = useState({});
  const [entranceRegisterID, setEntranceRegisterID] = useState({});
  const [moneyOut, setMoneyOut] = useState(Number());
  const [moneyIn, setMoneyIn] = useState(Number());
  const [exchangeRate, setExchangeRate] = useState(Number());
  const [date, setDate] = useState(new Date());
  const [description, setDescription] = useState("");
  const [transferAmount, setTransferAmount] = useState(0);

  useEffect(() => {
    setToken(localStorage.getItem("token") || sessionStorage.getItem("token"));
    if (user && user.country) {
      setCustomerID(user.country);
      api
        .get(
          `/api/Register/GetRegisters?customerID=${user.country}&pageNumber=1&pageSize=9999`
        )
        .then((res) => {
          const dataRegisters = res.data.items;
          const newDataRegisters = dataRegisters?.map((item) => ({
            value: item.registerID,
            label: item.name,
            value2: item.currencyID,
          }));
          setRegisters(newDataRegisters);
        })
        .catch((err) => console.log(err.data))
        .finally(() => setLoading(false));
    }
  }, [user]);

  const postData = {
    customerID,
    exitRegisterID: exitRegisterID.value,
    entranceRegisterID: entranceRegisterID.value,
    moneyOut,
    moneyIn,
    exchangeRate:
      exitRegisterID?.value2 === entranceRegisterID?.value2 ? 0 : exchangeRate,
    date,
    description,
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const storedToken =
        localStorage.getItem("token") || sessionStorage.getItem("token");
      if (!storedToken) {
        throw new Error("Token bulunamadı.");
      }

      await api.post(
        "/api/Register/PostRegisterTransfer/PostRegisterTransfer",
        postData,
        {
          headers: {
            Authorization: `Bearer ${storedToken}`,
          },
        }
      );

      Swal.fire({
        position: "top-end",
        icon: "success",
        title: "Transfer edildi !",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error("Hata alındı: ", error);
      const errorMessageKey = error.response?.data;
      const translatedError = errorMessageKey
        ? t(`errors.${errorMessageKey}`, { defaultValue: errorMessageKey })
        : t("errors.UnknownError", { defaultValue: "Bilinmeyen bir hata" });
      Swal.fire({
        position: "top-end",
        icon: "error",
        title: `${translatedError}`,
        showConfirmButton: false,
        timer: 1500,
      });
    } finally {
      setLoading(false);
      onCancel();
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    }
  };

  // Kasalar eşit mi kontrolü
  const isSameRegister =
    exitRegisterID?.value2 === entranceRegisterID?.value2 &&
    exitRegisterID &&
    entranceRegisterID;

  useEffect(() => {
    if (isSameRegister) {
      setMoneyOut(transferAmount);
      setMoneyIn(transferAmount);
    }
  }, [transferAmount, isSameRegister]);

  useEffect(() => {
    // Seçimler yapıldığında ve value'lar eşit olmadığında API isteği yap
    if (
      exitRegisterID &&
      entranceRegisterID &&
      exitRegisterID.value2 !== entranceRegisterID.value2
    ) {
      const girisCurrencyID = exitRegisterID.value2; // apiden dolayı yerlerini değiştirdim
      const cikisCurrencyID = entranceRegisterID.value2;

      api
        .get(
          `/api/Common/GetCurrencyExchangeRateTwo?giriscurrencyID=${girisCurrencyID}&cikiscurrencyID=${cikisCurrencyID}`
        )
        .then((res) => {
          setExchangeRate(res.data);
        })
        .catch((error) => {
          console.error("API Hatası:", error);
        });
    }
  }, [exitRegisterID, entranceRegisterID]); // Seçimler değiştiğinde çalışır

  useEffect(() => {
    // Eğer döviz kuru ve çıkan tutar uygun şekilde belirlenmişse giren tutarı hesapla
    if (exchangeRate && moneyOut) {
      setMoneyIn(moneyOut * exchangeRate);
    }
  }, [exchangeRate, moneyOut]); // exchangeRate veya moneyOut değiştiğinde tetiklenir

  return (
    <div className="fixed top-0 left-0 w-screen h-screen bg-black bg-opacity-30 z-50 flex justify-center items-center">
      <div className="w-3/5 p-8 bg-white rounded-lg flex flex-col justify-center items-center">
        {loading ? (
          <LoadingSpinnerModal loading={loading} />
        ) : (
          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-2 gap-2 items-center"
          >
            <div className="col-span-2">
              <h1 className="text-center  text-4xl font-bold leading-none tracking-tight text-blue-800 pb-10">
                Kasalar Arası Transfer
              </h1>
            </div>
            <div className="col-span-2">
              <label className="block mb-2 text-sm font-medium text-gray-900">
                Çıkış Kasa
              </label>
              <Select
                maxMenuHeight={200}
                classNamePrefix="custom-select"
                placeholder="Seçiniz..."
                options={registers}
                value={exitRegisterID}
                onChange={(e) => setExitRegisterID(e)}
              />
            </div>
            <div className="col-span-2">
              <label className="block mb-2 text-sm font-medium text-gray-900">
                Giriş Kasa
              </label>
              <Select
                maxMenuHeight={200}
                classNamePrefix="custom-select"
                placeholder="Seçiniz..."
                options={registers.filter(
                  (item) => item.value !== exitRegisterID?.value
                )}
                value={entranceRegisterID}
                onChange={(e) => setEntranceRegisterID(e)}
              />
            </div>

            <div className="col-span-2">
              <label
                htmlFor="visitors"
                className="block mb-2 text-sm font-medium text-gray-900"
              >
                İşlem Tarihi
              </label>
              <DatePicker
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                locale={tr}
                dateFormat={"dd/MM/yy"}
                selected={date}
                onChange={(e) => setDate(e)}
              />
            </div>
            {!isSameRegister ? (
              <>
                <div className="col-span-2">
                  <label className="block mb-2 text-sm font-medium text-gray-900">
                    Döviz Kuru :
                  </label>
                  <input
                    type="number"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                    value={exchangeRate}
                    onChange={(e) =>
                      setExchangeRate(parseFloat(e.target.value))
                    }
                  />
                </div>
                <div className="col-span-2">
                  <label className="block mb-2 text-sm font-medium text-gray-900">
                    Çıkan Tutar :
                  </label>
                  <input
                    type="number"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                    value={moneyOut}
                    onChange={(e) => setMoneyOut(parseFloat(e.target.value))}
                  />
                </div>
                <div className="col-span-2">
                  <label className="block mb-2 text-sm font-medium text-gray-900">
                    Giren Tutar :
                  </label>
                  <input
                    type="number"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                    value={moneyIn}
                    disabled
                  />
                </div>
              </>
            ) : (
              <div className="col-span-2">
                <label className="block mb-2 text-sm font-medium text-gray-900">
                  Gönderilecek Tutar :
                </label>
                <input
                  type="number"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                  value={transferAmount}
                  onChange={(e) =>
                    setTransferAmount(parseFloat(e.target.value))
                  }
                />
              </div>
            )}
            <div className="col-span-2">
              <label
                className="block mb-2 text-sm font-medium text-gray-900"
                htmlFor="description"
              >
                Açıklama :
              </label>
              <textarea
                id="description"
                type="text"
                className="resize-none bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                rows={4}
              />
            </div>

            <div className="col-span-2 flex justify-center gap-[20px]">
              <button
                onClick={onCancel}
                className="bg-[#B10F0F] hover:bg-[#b10f0fd8] text-white font-medium py-2 px-4 rounded-lg m-5 h-12 w-32"
              >
                Vazgeç
              </button>
              <button
                type="submit"
                className="bg-[#0C692F] hover:bg-[#0c6930d3] text-white font-medium py-2 px-4 rounded-lg m-5 h-12 w-36"
              >
                Transfer Yap
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default FinancialRegisterTransferModal;
